/* eslint-disable no-restricted-syntax */
/* eslint-disable no-cond-assign */

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import * as ProposalNS from '../../Proposal';

import * as ProposalElementNS from '../../ProposalElements';

import { DataPoints, DataPointResponse } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { FIXED_INCOME_SECTORS, FIXED_INCOME_SECTOR_GROUPS, ASSET_ALLOCATION_GROUPS } from '../../../../utils/constants';
import { LOCALE } from '../../../../utils/i18n';
import { PercentageCell, NotApplicableCell } from '../../../../views/components/cells';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import { getKeyByValue } from '../../../../utils/objects';
import Dictionary from '@/types/dictionary';
import DonutData from '@/types/donut-data';
import LocalizedString from '@/types/localized-string';
import AppRegion from '@/types/app-region';

export default class FixedIncomeSectorAllocationDAO extends ComparisonItemBasedProposalDAO {
  groupValues: Dictionary<Array<number>> | null = null;

  fixedIncomeSectorKeys: Dictionary<string> = { ...FIXED_INCOME_SECTORS, OTHER: 'Other' };

  sectorValues: Dictionary<Array<number>> = {
    [this.fixedIncomeSectorKeys.CAN_CORP_BONDS]: [],
    [this.fixedIncomeSectorKeys.CAN_GOV_BONDS]: [],
    [this.fixedIncomeSectorKeys.CAN_BONDS_OTHER]: [],
    [this.fixedIncomeSectorKeys.FOR_CORP_BONDS]: [],
    [this.fixedIncomeSectorKeys.FOR_GOV_BONDS]: [],
    [this.fixedIncomeSectorKeys.FOR_BONDS_OTHER]: [],
    [this.fixedIncomeSectorKeys.CASH]: [],
  };

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    const dao = new FixedIncomeSectorAllocationDAO(proposal, proposalElement);

    if (proposal.appUser.appConfig.appRegion === AppRegion.US) {
      dao.fixedIncomeSectorKeys = {
        GOVERNMENT: 'Government',
        MUNICIPAL: 'Municipal',
        CORPORATE: 'Corporate',
        SECURITIZED: 'Securitized',
        CASH: 'Cash & Equivalents',
        DERIVATIVE: 'Derivative',
      };

      dao.sectorValues = {
        [dao.fixedIncomeSectorKeys.GOVERNMENT]: [],
        [dao.fixedIncomeSectorKeys.MUNICIPAL]: [],
        [dao.fixedIncomeSectorKeys.CORPORATE]: [],
        [dao.fixedIncomeSectorKeys.SECURITIZED]: [],
        [dao.fixedIncomeSectorKeys.CASH]: [],
        [dao.fixedIncomeSectorKeys.DERIVATIVE]: [],
      };
    }

    return dao;
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['FIXED_INCOME_SECTOR_GROUP_COLORS', 'DEFAULT'];
  }

  // eslint-disable-next-line class-methods-use-this
  private hasEmptySectors(itemDAO: ComparisonItemDAO) {
    const fixedIncomeAllocation = DataPoints.getAssetAllocationTotalsByCategory(itemDAO.item, ASSET_ALLOCATION_GROUPS.FIXED_INCOME).response;
    return (
      fixedIncomeAllocation > 0 &&
      itemDAO.item.allocations &&
      itemDAO.item.allocations.fixedIncomeSector &&
      itemDAO.item.allocations.fixedIncomeSector.length === 0
    );
  }

  protected build(): void {
    let filterCount: number = 10;
    let start: number = 1;
    let omit: string[] = [];
    let orderSectorsByValue = false;

    const daoConfig: ProposalNS.FixedIncomeSectorAllocationConfig = this.proposalElement.DAO as ProposalNS.FixedIncomeSectorAllocationConfig;
    if (daoConfig.groupByFixedIncomeSectorGroupDAOOptions !== undefined) {
      if (daoConfig.groupByFixedIncomeSectorGroupDAOOptions === true) {
        this.groupValues = {
          [FIXED_INCOME_SECTOR_GROUPS.CASH]: [],
          [FIXED_INCOME_SECTOR_GROUPS.GOVERNMENT]: [],
          [FIXED_INCOME_SECTOR_GROUPS.CORPORATE]: [],
          [FIXED_INCOME_SECTOR_GROUPS.OTHER]: [],
        };
      }
    }

    if (daoConfig.filterTopXDAOOptions !== undefined) {
      filterCount = daoConfig.filterTopXDAOOptions.count;

      if (daoConfig.filterTopXDAOOptions.start) {
        start = daoConfig.filterTopXDAOOptions.start;
      }

      omit = daoConfig.filterTopXDAOOptions.omit || [];
    }

    if (daoConfig.orderFixedIncomeSectorsByValueDAOOptions !== undefined) {
      orderSectorsByValue = daoConfig.orderFixedIncomeSectorsByValueDAOOptions;
    }

    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      let sectorKeys: LocalizedString[] = DataPoints.getFixedIncomeSectorAllocationsKeys(itemDAO.item);

      if (orderSectorsByValue) {
        this.sectorValues = sectorKeys.reduce(
          (acc: Dictionary<number[]>, key) => ({
            ...acc,
            [key[LOCALE.EN_CA] || '']: [],
          }),
          {},
        );
      }

      if (omit.length) {
        sectorKeys = sectorKeys.filter(key => !omit.some(val => (key['en-CA'] ? key['en-CA'] === val : false)));
      }

      for (let i = start; i < start + filterCount; i++) {
        const enumKey = Object.keys(this.sectorValues)[i - 1];
        if (Object.prototype.hasOwnProperty.call(this.sectorValues, enumKey)) {
          const sectorClass: LocalizedString | undefined = sectorKeys.find(sectorKey => sectorKey[LOCALE.EN_CA] === enumKey);
          if (sectorClass !== undefined) {
            const dpResponse: DataPointResponse<number> = DataPoints.getFixedIncomeSectorAllocationByKey(itemDAO.item, enumKey);
            this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);
            this.sectorValues[enumKey].push(dpResponse.response);
          } else if (this.hasEmptySectors(itemDAO) && enumKey === FIXED_INCOME_SECTOR_GROUPS.OTHER) {
            this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);
            this.sectorValues[enumKey].push(100);
          } else {
            this.sectorValues[enumKey].push(0);
          }
        }
      }
    }

    if (!daoConfig.showEmptySectors) {
      for (const enumKey in this.sectorValues) {
        if (Object.prototype.hasOwnProperty.call(this.sectorValues, enumKey)) {
          if (this.sectorValues[enumKey].find(value => value !== 0) === undefined) {
            delete this.sectorValues[enumKey];
          }
        }
      }
    }

    if (this.groupValues !== null) {
      const nbItems: number = this.getItemDAOs().length;
      for (const sectorGroupKey of Object.keys(this.groupValues)) {
        for (let i: number = 0; i < nbItems; i++) {
          this.groupValues[sectorGroupKey].push(0);
        }
      }

      for (const sectorKey in this.sectorValues) {
        if (Object.prototype.hasOwnProperty.call(this.sectorValues, sectorKey)) {
          const key = getKeyByValue(this.fixedIncomeSectorKeys, sectorKey);
          if (key !== undefined) {
            this.addToGroup(key, this.sectorValues[sectorKey]);
          }
        }
      }
      if (!daoConfig.showEmptySectors) {
        for (const enumKey in this.groupValues) {
          if (Object.prototype.hasOwnProperty.call(this.groupValues, enumKey)) {
            if (this.groupValues[enumKey].find(value => value !== 0) === undefined) {
              delete this.groupValues[enumKey];
            }
          }
        }
      }
    }

    this.resetItemIndex();
    while ((itemDAO = this.nextItem()) !== null) {
      if (this.groupValues === null) {
        for (const sectorKey of Object.keys(this.sectorValues)) {
          const key = getKeyByValue(this.fixedIncomeSectorKeys, sectorKey);
          let fieldLabel = this.formatMessage(`FixedIncomeSectorAllocations.${key}`);
          let value = this.sectorValues[sectorKey][this.itemIndex()];

          if (this.hasEmptySectors(itemDAO) && sectorKey === FIXED_INCOME_SECTOR_GROUPS.OTHER) {
            this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);
            value = 100;
            fieldLabel += '*';
          }

          const field = new ProposalFieldDAO(fieldLabel, value, ProposalStyleClassNames.ProposalTableContent, value === 0 ? NotApplicableCell : PercentageCell);
          field.key = sectorKey;
          this.addFieldDAO(itemDAO, field);
          const color = (this.colorsDict && sectorKey && this.colorsDict[sectorKey]) || undefined;
          this.addFieldName(fieldLabel || '', ProposalStyleClassNames.ProposalTableContent, color);
        }
      } else {
        for (const sectorGroupKey of Object.keys(this.groupValues)) {
          let value = this.groupValues[sectorGroupKey][this.itemIndex()];
          let fieldLabel = this.formatMessage(`FixedIncomeSectorAllocationGroups.${sectorGroupKey}`);

          if (this.hasEmptySectors(itemDAO) && sectorGroupKey === FIXED_INCOME_SECTOR_GROUPS.OTHER) {
            this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);
            value = 100;
            fieldLabel += '*';
          }

          const field = new ProposalFieldDAO(fieldLabel, value, ProposalStyleClassNames.ProposalTableContent, value === 0 ? NotApplicableCell : PercentageCell);
          field.key = sectorGroupKey;
          this.addFieldDAO(itemDAO, field);
          const color = (this.colorsDict && sectorGroupKey && this.colorsDict[sectorGroupKey]) || undefined;
          this.addFieldName(fieldLabel || '', ProposalStyleClassNames.ProposalTableContent, color);
        }
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }

    return out;
  }

  private addToGroup(sectorKey: string | undefined, value: number[]) {
    if (this.groupValues !== null) {
      switch (sectorKey) {
        case 'CAN_CORP_BONDS':
        case 'FOR_CORP_BONDS': {
          this.groupValues[FIXED_INCOME_SECTOR_GROUPS.CORPORATE] = this.groupValues[FIXED_INCOME_SECTOR_GROUPS.CORPORATE].map((v, index) => v + value[index]);
          break;
        }
        case 'CAN_GOV_BONDS':
        case 'FOR_GOV_BONDS': {
          this.groupValues[FIXED_INCOME_SECTOR_GROUPS.GOVERNMENT] = this.groupValues[FIXED_INCOME_SECTOR_GROUPS.GOVERNMENT].map((v, index) => v + value[index]);
          break;
        }
        case 'CAN_BONDS_OTHER':
        case 'OTHER':
        case 'FOR_BONDS_OTHER': {
          this.groupValues[FIXED_INCOME_SECTOR_GROUPS.OTHER] = this.groupValues[FIXED_INCOME_SECTOR_GROUPS.OTHER].map((v, index) => v + value[index]);
          this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER]);
          break;
        }
        case 'CASH': {
          this.groupValues[FIXED_INCOME_SECTOR_GROUPS.CASH] = this.groupValues[FIXED_INCOME_SECTOR_GROUPS.CASH].map((v, index) => v + value[index]);
          break;
        }
      }
    }
  }
}
